'use client';

import { memo, useMemo, useCallback, useState, useRef, useEffect } from 'react';
import { cn, Checkbox, Label } from '@crabs-ai/ds-react';
import {
  FolderFill,
  AudioFileFill,
  PlayArrowFill,
  PauseFill,
  BookmarkFill,
} from '@crabs-ai/web-icons';
import { useTranslations } from 'next-intl';
import { AnimatePresence, motion } from 'motion/react';
import {
  GetFolderItemsContentResponseType,
  GetFolderItemsFolderResponseType,
  FolderItemsResponseType,
} from '@/shared/apis/folder/get-folder-items';
import { DtoContentType, DtoFolderItemType } from '@/shared/constants';
import { convertMillisecondsToTimeFormat } from '@/shared';
import ImageWithFallback from '@/shared/ui/images/ImageWithFallback';
import { FolderType } from './useAssetPicker';
import { FOLDER_TYPE_TRANSLATION_KEY } from './folder-type-constants';

// 다른 에셋 재생 시 현재 재생 중인 에셋 중지를 위한 이벤트
const ASSET_PICKER_PLAY_EVENT = 'asset-picker-item-play';

export interface AssetPickerCardProps {
  item: FolderItemsResponseType;
  isSelected?: boolean;
  isBookmarked?: boolean;
  showTypeLabel?: boolean;
  onSelect?: (item: GetFolderItemsContentResponseType) => void;
  onFolderClick?: (item: GetFolderItemsFolderResponseType) => void;
}

/**
 * 에셋 선택 모달용 카드 컴포넌트
 * - 폴더: 클릭 시 해당 폴더로 이동
 * - 콘텐츠: 클릭 시 선택/해제 토글, 체크박스 표시
 * - 비디오/오디오: 재생 기능
 */
export const AssetPickerCard = memo(function AssetPickerCard({
  item,
  isSelected = false,
  isBookmarked = false,
  showTypeLabel = false,
  onSelect,
  onFolderClick,
}: AssetPickerCardProps) {
  const t = useTranslations();
  const isFolder = item.type === DtoFolderItemType.folder;
  const isContent = item.type === DtoFolderItemType.content;

  // 재생 관련 상태
  const [isPlaying, setIsPlaying] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const itemIdRef = useRef(item.id);

  // 콘텐츠 타입 확인
  const contentItem = isContent
    ? (item as GetFolderItemsContentResponseType)
    : null;
  const isVideo = contentItem?.contentType === DtoContentType.video;
  const isAudio = contentItem?.contentType === DtoContentType.audio;
  const isPlayable = isVideo || isAudio;

  // 타입 라벨 (전체 필터일 때 표시)
  const contentTypeLabel = useMemo(() => {
    if (!contentItem) return null;
    const typeConfig: Record<string, string> = {
      [DtoContentType.video]: t('Editor.asset-item.video'),
      [DtoContentType.image]: t('Editor.asset-item.image'),
      [DtoContentType.audio]: t('Editor.asset-item.audio'),
    };
    return typeConfig[contentItem.contentType] ?? null;
  }, [contentItem, t]);

  // 시스템 폴더의 경우 번역된 이름 사용
  const displayName = useMemo(() => {
    if (isFolder) {
      const folderItem = item as GetFolderItemsFolderResponseType;
      const folderType = folderItem.folderType as FolderType;
      const translationKey = FOLDER_TYPE_TRANSLATION_KEY[folderType];
      if (translationKey && folderType !== FolderType.CUSTOM) {
        return t(translationKey);
      }
    }
    return item.name;
  }, [isFolder, item, t]);

  // 다른 에셋 재생 시 현재 재생 중지
  useEffect(() => {
    const handleOtherPlay = (e: CustomEvent<number>) => {
      if (e.detail !== itemIdRef.current && isPlaying) {
        const mediaRef = isVideo ? videoRef : audioRef;
        if (mediaRef.current) {
          mediaRef.current.pause();
        }
        setIsPlaying(false);
      }
    };

    window.addEventListener(
      ASSET_PICKER_PLAY_EVENT,
      handleOtherPlay as EventListener,
    );
    return () => {
      window.removeEventListener(
        ASSET_PICKER_PLAY_EVENT,
        handleOtherPlay as EventListener,
      );
    };
  }, [isPlaying, isVideo]);

  // 재생/일시정지 핸들러
  const handlePlayClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();

      const mediaRef = isVideo ? videoRef : audioRef;
      if (mediaRef.current) {
        if (isPlaying) {
          mediaRef.current.pause();
          setIsPlaying(false);
        } else {
          window.dispatchEvent(
            new CustomEvent(ASSET_PICKER_PLAY_EVENT, { detail: item.id }),
          );
          mediaRef.current.currentTime = 0;
          mediaRef.current
            .play()
            .then(() => {
              setIsPlaying(true);
            })
            .catch(() => {
              setIsPlaying(false);
            });
        }
      }
    },
    [isPlaying, isVideo, item.id],
  );

  const handleMouseEnter = useCallback(() => {
    setIsHovered(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  const handleMediaEnded = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleClick = useCallback(() => {
    if (isFolder && onFolderClick) {
      onFolderClick(item as GetFolderItemsFolderResponseType);
    } else if (isContent && onSelect) {
      onSelect(item as GetFolderItemsContentResponseType);
    }
  }, [isFolder, isContent, item, onFolderClick, onSelect]);

  const handleCheckboxClick = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isContent && onSelect) {
        onSelect(item as GetFolderItemsContentResponseType);
      }
    },
    [isContent, item, onSelect],
  );

  // 썸네일 렌더링
  const thumbnailNode = useMemo(() => {
    if (isFolder) {
      return (
        <div className="flex aspect-video w-full items-center justify-center bg-sys-style-neutral-lightest">
          <FolderFill className="size-3xl [&_path]:fill-sys-style-primary-default" />
        </div>
      );
    }

    if (!contentItem) return null;

    if (contentItem.contentType === DtoContentType.audio) {
      return (
        <div className="flex aspect-video w-full items-center justify-center bg-sys-style-neutral-light">
          <AudioFileFill className="size-2xl [&_path]:fill-sys-cont-neutral-disabled-light" />
          <audio
            ref={audioRef}
            src={contentItem.publicUrl ?? ''}
            preload="none"
            onEnded={handleMediaEnded}
            className="hidden"
          />
        </div>
      );
    }

    if (contentItem.contentType === DtoContentType.image) {
      return (
        <div className="relative aspect-video w-full">
          <ImageWithFallback
            src={contentItem.publicUrl || ''}
            alt={contentItem.name}
            fill
            className="object-cover"
          />
        </div>
      );
    }

    // 비디오
    return (
      <div className="relative aspect-video w-full overflow-hidden">
        {/* 비디오 플레이어 */}
        <video
          ref={videoRef}
          src={contentItem.publicUrl ?? ''}
          preload="none"
          className={cn(
            'absolute inset-0 h-full w-full bg-black object-contain',
            !isPlaying && 'hidden',
          )}
          onEnded={handleMediaEnded}
        />
        {/* 썸네일 */}
        <div className={cn(isPlaying && 'hidden')}>
          <ImageWithFallback
            src={contentItem.thumbnailPublicUrl || ''}
            alt={contentItem.name}
            fill
            className="object-cover"
          />
        </div>
      </div>
    );
  }, [isFolder, contentItem, isPlaying, handleMediaEnded]);

  // 재생 버튼 렌더링
  const playButtonNode = useMemo(() => {
    if (!isPlayable || !isHovered) return null;

    return (
      <AnimatePresence>
        <motion.button
          type="button"
          onClick={handlePlayClick}
          className={cn(
            'absolute top-1/2 left-1/2 z-20 -translate-x-1/2 -translate-y-1/2',
            'flex size-xl items-center justify-center rounded-full',
            'bg-black/60 transition-all hover:bg-black/80',
            'cursor-pointer',
          )}
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.9 }}
          transition={{ duration: 0.1, ease: 'easeOut' }}
        >
          {isPlaying ? (
            <PauseFill className="size-md [&_path]:fill-white" />
          ) : (
            <PlayArrowFill className="size-md [&_path]:fill-white" />
          )}
        </motion.button>
      </AnimatePresence>
    );
  }, [isPlayable, isHovered, isPlaying, handlePlayClick]);

  // 메타데이터 라벨 (비디오/오디오 duration)
  const metadataNode = useMemo(() => {
    if (isFolder || !contentItem) return null;

    if (
      contentItem.contentType === DtoContentType.image ||
      !contentItem.duration
    ) {
      return null;
    }

    return (
      <div className="bg-sys-surf-container-highest/70 absolute bottom-2xs left-2xs z-10 flex items-center gap-4xs rounded-xs px-4xs py-5xs">
        <PlayArrowFill className="size-sm [&_path]:fill-sys-cont-inverse-light" />
        <span className="text-comp-accent-xsmall text-sys-cont-inverse-light">
          {convertMillisecondsToTimeFormat(contentItem.duration)}
        </span>
      </div>
    );
  }, [isFolder, contentItem]);

  return (
    <div
      className={cn(
        'relative cursor-pointer overflow-hidden rounded-xs border-2 bg-sys-bg-surface transition-all hover:shadow-md',
        isSelected
          ? 'border-sys-style-primary-default'
          : 'border-transparent hover:border-sys-style-neutral-default',
      )}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {/* 체크박스 (콘텐츠만) */}
      {isContent && (
        <div
          className="absolute top-2xs left-2xs z-10"
          onClick={handleCheckboxClick}
        >
          <Checkbox checked={isSelected} />
        </div>
      )}

      {/* 선택 오버레이 */}
      {isSelected && (
        <div className="pointer-events-none absolute inset-0 z-[5] bg-sys-style-primary-default/10" />
      )}

      {/* 썸네일 + 메타데이터 라벨 + 재생 버튼 + 타입 라벨 + 북마크 */}
      <div className="relative">
        {thumbnailNode}
        {metadataNode}
        {playButtonNode}
        {/* 타입 라벨 (전체 필터일 때만 표시) - 북마크 왼쪽 */}
        {showTypeLabel && contentTypeLabel && (
          <div className="absolute top-2xs right-[36px] z-30">
            <Label
              color="primary"
              isHighlight
              size="small"
              type="default"
            >
              {contentTypeLabel}
            </Label>
          </div>
        )}
        {/* 북마크 아이콘 (우측 상단) - my-space 디자인 통일 */}
        {isBookmarked && (
          <div className="bg-sys-surf-container-highest/70 absolute top-5xs right-5xs z-30 rounded-full p-4xs">
            <BookmarkFill className="size-md [&_path]:fill-sys-style-primary-default" />
          </div>
        )}
      </div>

      {/* 이름 */}
      <div className="p-2xs">
        <p className="truncate text-caption-small text-sys-cont-neutral-default">
          {displayName}
        </p>
      </div>
    </div>
  );
});

export default AssetPickerCard;
